/** CSS variables applied on :root; colorTokens references these for company-scoped UI themes. */

export const ZEKAY_THEME_CSS_VAR_KEYS = [
  "--zekay-bg-app",
  "--zekay-bg-surface",
  "--zekay-bg-surface-elevated",
  "--zekay-bg-surface-muted",
  "--zekay-bg-hover",
  "--zekay-border-default",
  "--zekay-border-strong",
  "--zekay-border-focus",
  "--zekay-accent-primary",
  "--zekay-accent-hover",
  "--zekay-accent-contrast-text",
] as const;

export type ZekayThemeCssVarKey = (typeof ZEKAY_THEME_CSS_VAR_KEYS)[number];

export type ZekayThemePresetId =
  | "ocean_blue"
  | "deep_purple"
  | "teal_carbon"
  | "warm_slate"
  | "forest_night"
  | "amber_glow";

export type ZekayThemeVarMap = Record<ZekayThemeCssVarKey, string>;

export const ZEKAY_THEME_PRESETS: Record<ZekayThemePresetId, ZekayThemeVarMap> = {
  ocean_blue: {
    "--zekay-bg-app": "#101826",
    "--zekay-bg-surface": "#162236",
    "--zekay-bg-surface-elevated": "#1C2B44",
    "--zekay-bg-surface-muted": "#243552",
    "--zekay-bg-hover": "#2D4367",
    "--zekay-border-default": "#35507A",
    "--zekay-border-strong": "#446293",
    "--zekay-border-focus": "#5FA8FF",
    "--zekay-accent-primary": "#5FA8FF",
    "--zekay-accent-hover": "#4A97F2",
    "--zekay-accent-contrast-text": "#08111F",
  },
  deep_purple: {
    "--zekay-bg-app": "#14101F",
    "--zekay-bg-surface": "#1E1830",
    "--zekay-bg-surface-elevated": "#261F3D",
    "--zekay-bg-surface-muted": "#2F2848",
    "--zekay-bg-hover": "#3D3458",
    "--zekay-border-default": "#4A3D6B",
    "--zekay-border-strong": "#5C4D82",
    "--zekay-border-focus": "#C4A7F7",
    "--zekay-accent-primary": "#B794F6",
    "--zekay-accent-hover": "#A78BDA",
    "--zekay-accent-contrast-text": "#0F0A18",
  },
  teal_carbon: {
    "--zekay-bg-app": "#0F1A1A",
    "--zekay-bg-surface": "#152828",
    "--zekay-bg-surface-elevated": "#1C3333",
    "--zekay-bg-surface-muted": "#254040",
    "--zekay-bg-hover": "#2F4F4F",
    "--zekay-border-default": "#2D5A5A",
    "--zekay-border-strong": "#3D6D6D",
    "--zekay-border-focus": "#2DD4BF",
    "--zekay-accent-primary": "#2DD4BF",
    "--zekay-accent-hover": "#26B8A8",
    "--zekay-accent-contrast-text": "#051014",
  },
  warm_slate: {
    "--zekay-bg-app": "#1C1816",
    "--zekay-bg-surface": "#252220",
    "--zekay-bg-surface-elevated": "#2E2926",
    "--zekay-bg-surface-muted": "#3A3430",
    "--zekay-bg-hover": "#4A433C",
    "--zekay-border-default": "#5C524A",
    "--zekay-border-strong": "#6E6359",
    "--zekay-border-focus": "#E8A87C",
    "--zekay-accent-primary": "#E8A87C",
    "--zekay-accent-hover": "#D99868",
    "--zekay-accent-contrast-text": "#1A1208",
  },
  forest_night: {
    "--zekay-bg-app": "#101814",
    "--zekay-bg-surface": "#15251C",
    "--zekay-bg-surface-elevated": "#1C3024",
    "--zekay-bg-surface-muted": "#243D2E",
    "--zekay-bg-hover": "#2F4D3A",
    "--zekay-border-default": "#355C45",
    "--zekay-border-strong": "#447058",
    "--zekay-border-focus": "#6EE7A0",
    "--zekay-accent-primary": "#5EEAD4",
    "--zekay-accent-hover": "#4DD4BF",
    "--zekay-accent-contrast-text": "#041A12",
  },
  amber_glow: {
    "--zekay-bg-app": "#16100A",
    "--zekay-bg-surface": "#1F160E",
    "--zekay-bg-surface-elevated": "#2A2016",
    "--zekay-bg-surface-muted": "#362A20",
    "--zekay-bg-hover": "#443528",
    "--zekay-border-default": "#5C4836",
    "--zekay-border-strong": "#6E5644",
    "--zekay-border-focus": "#FCD34D",
    "--zekay-accent-primary": "#FBBF24",
    "--zekay-accent-hover": "#F59E0B",
    "--zekay-accent-contrast-text": "#1A0F06",
  },
};

export const ZEKAY_THEME_PRESET_ORDER: ZekayThemePresetId[] = [
  "ocean_blue",
  "deep_purple",
  "teal_carbon",
  "warm_slate",
  "forest_night",
  "amber_glow",
];

export const ZEKAY_THEME_PRESET_OPTIONS: { id: ZekayThemePresetId; label: string }[] = [
  { id: "ocean_blue", label: "Bleu marine sombre" },
  { id: "deep_purple", label: "Violet et indigo" },
  { id: "teal_carbon", label: "Turquoise et bleu-vert" },
  { id: "warm_slate", label: "Gris chaud et ambre" },
  { id: "forest_night", label: "Vert forêt sombre" },
  { id: "amber_glow", label: "Jaune ambre et orange" },
];

const PRESET_ID_SET = new Set<string>(ZEKAY_THEME_PRESET_ORDER);

export function normalizePresetFromApi(raw: string | undefined): ZekayThemePresetId {
  const v = (raw ?? "").trim().toLowerCase();
  if (PRESET_ID_SET.has(v)) return v as ZekayThemePresetId;
  return "ocean_blue";
}

/** Accent color used as the dominant swatch in theme preset pickers. */
export function themePresetDominantSwatchHex(id: ZekayThemePresetId): string {
  return ZEKAY_THEME_PRESETS[id]["--zekay-accent-primary"];
}

export function applyZekayThemePreset(id: ZekayThemePresetId): void {
  const vars = ZEKAY_THEME_PRESETS[id];
  if (vars == null || typeof document === "undefined") return;
  const root = document.documentElement;
  for (const key of ZEKAY_THEME_CSS_VAR_KEYS) {
    root.style.setProperty(key, vars[key]);
  }
}

export function getZekayThemeRootCssText(vars: ZekayThemeVarMap): string {
  return ZEKAY_THEME_CSS_VAR_KEYS.map((key) => `    ${key}: ${vars[key]};`).join("\n");
}
